import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook'
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin'
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter'
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import copy from 'copy-to-clipboard'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { baseDomain } from '../../util/baseDomain'

export const copyFullUrl = (): void => {
  if (window && window.location && window.location.href) {
    copy(window.location.href.split('#')[0])
  }
}

const socialLink = (
  socialPlatform: 'Twitter' | 'LinkedIn' | 'Facebook',
  articleUrl: string,
  title?: string,
) => {
  switch (socialPlatform) {
    case 'Twitter':
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        title || articleUrl,
      )}%20by%20%40the_sukha_co%20${encodeURIComponent(articleUrl)}`
    case 'LinkedIn':
      return `https://www.linkedin.com/shareArticle/?mini=true&url=${encodeURIComponent(
        articleUrl,
      )}`
    case 'Facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        articleUrl,
      )}`
    default:
      return
  }
}

interface SharePostProps {
  link: string
  title: string
}

export default function SharePost({
  title,
  link,
}: SharePostProps): JSX.Element {
  const [urlCopyState, copyPostUrl] = useCopyPostUrl()

  const fullUrlPath = baseDomain() + link

  function handleLinkedinClick() {
    const shareUrl = socialLink('LinkedIn', fullUrlPath)
    window.open(
      shareUrl,
      '',
      '_blank, width=500, height=500, resizable=yes, scrollbars=yes',
    )
  }

  return (
    <div className="space-y-2">
      <button className="flex items-center" onClick={copyPostUrl}>
        <FontAwesomeIcon icon={urlCopyState === 'copied' ? faCheck : faLink} />

        <div className="ml-3">
          {urlCopyState === 'copied' ? 'Copied' : 'Copy Link'}
        </div>
      </button>

      <a
        href={socialLink('Twitter', fullUrlPath, title)}
        rel="noreferrer"
        target="_blank"
        className="flex items-center"
      >
        <FontAwesomeIcon icon={faTwitter} />

        <p className="ml-4">Twitter</p>
      </a>

      <button className="flex items-center" onClick={handleLinkedinClick}>
        <FontAwesomeIcon icon={faLinkedin} />

        <p className="ml-4">LinkedIn</p>
      </button>

      <a
        href={socialLink('Facebook', fullUrlPath, title)}
        rel="noreferrer"
        target="_blank"
        className="flex items-center"
      >
        <FontAwesomeIcon icon={faFacebook} />

        <p className="ml-4">Facebook</p>
      </a>

      <Link href="/" className="flex items-center">
        <FontAwesomeIcon icon={faDownload} />

        <p className="ml-4">Get Sukha</p>
      </Link>
    </div>
  )
}

type CopyUrlStates = 'idle' | 'copied'

function useCopyPostUrl(): [CopyUrlStates, () => void] {
  const [linkWasCopied, setLinkWasCopied] = useState<CopyUrlStates>('idle')

  function handleCopyLinkClick() {
    copyFullUrl()
    setLinkWasCopied('copied')
  }

  useEffect(() => {
    if (linkWasCopied) {
      const timer = setTimeout(() => {
        setLinkWasCopied('idle')
      }, 5000)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [linkWasCopied])

  return [linkWasCopied, handleCopyLinkClick]
}
